import { Container, Grid } from '@mui/material';
import {FC, useCallback, useContext, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';
import { Button, ToastSeverity, useToast } from 'thermia-react-container';
import { RegistrationsContext } from '../../context/contextProvider';
import useIsMounted from '../../hooks/useIsMounted';
import { ApiErrorHelper } from '../../validation/Errors';
import Wrapper from './containers/wrapper';

const Registrations: FC = () => {
  const isMounted = useIsMounted();
  const { registrations } = useContext(RegistrationsContext);
  const {
    fetchAllRegistrations,
    registrations: registrationsData,
    isRegistrationsLoading,
    registrationsHasBeenFetched
  } = registrations;
  const loading = useRef<boolean>(false);

  const { t } = useTranslation('registrations');
  const { addToast } = useToast();
  const theme = useTheme();

  const handleFetchRegistrations = useCallback(async () => {
    if(loading.current) {
      return;
    }
    loading.current = true;
    try {
      await fetchAllRegistrations();
    } catch (e: any) {
      if (isMounted) {
        if (ApiErrorHelper.isOnsiteVersionInvalid(e)) {
          addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
        } else {
          addToast(t('registrations.general.errorFetchRegistrations'), ToastSeverity.ERROR);
        }
      }
    } finally {
      loading.current = false;
    }
  }, [isMounted, fetchAllRegistrations, addToast, t]);

  useEffect(() => {
    if(!registrationsHasBeenFetched) {
      handleFetchRegistrations();
    }
  }, [registrationsHasBeenFetched, handleFetchRegistrations]);

  return (
    <Container maxWidth="xl" sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(50) }}>
      {registrationsData && <Wrapper registrations={registrationsData} fetching={isRegistrationsLoading} />}
      {!isRegistrationsLoading && !registrationsData && (
        <Grid container justifyContent={'center'}>
          <Button
            variant="contained"
            style={{ backgroundColor: theme.palette.error.main }}
            title={`${t('registrations.general.errorFetchRegistrations')} - ${t('registrations.general.retry')}`}
            loading={isRegistrationsLoading}
            onClick={handleFetchRegistrations}
          />
        </Grid>
      )}
    </Container>
  );
};

export default Registrations;
